import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import {
  DEFAULT_APURACAO_FILTERS,
  DEFAULT_APURACAO_SUMMARY,
  DEFAULT_EVENTOS_REJEITADOS_FILTERS,
  type Apuracao,
  type ApuracaoListFilters,
  type ApuracaoListSummary,
  type ApuracoesState,
  type EventoConsumo,
  type EventosRejeitadosFilters,
  type RejectedInvoice,
} from '../types';
import {
  fecharCicloApuracao,
  fetchApuracaoById,
  fetchApuracoes,
  fetchEventoDetalhe,
  fetchRejeitadosDoCiclo,
  marcarApuracaoFaturada,
  reabrirCicloApuracao,
  reemitirFaturaRejeitada,
  reprocessarEventos,
} from './actions';

interface ExtendedState extends ApuracoesState {
  rejectedInvoices: RejectedInvoice[];
  rejectedInvoicesLoading: boolean;
  eventoDetalhe: EventoConsumo | null;
  eventoDetalheLoading: boolean;
}

const initialState: ExtendedState = {
  apuracoes: [],
  summary: DEFAULT_APURACAO_SUMMARY,
  filters: DEFAULT_APURACAO_FILTERS,
  eventosRejeitadosFilters: DEFAULT_EVENTOS_REJEITADOS_FILTERS,
  rejectedInvoices: [],
  rejectedInvoicesLoading: false,
  eventoDetalhe: null,
  eventoDetalheLoading: false,
  loading: false,
  saving: false,
  error: null,
};

const computeSummary = (apuracoes: Apuracao[]): ApuracaoListSummary => {
  const now = Date.now();
  const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
  const startOfMonth = new Date();
  startOfMonth.setDate(1);
  startOfMonth.setHours(0, 0, 0, 0);
  const startOfMonthMs = startOfMonth.getTime();

  return apuracoes.reduce(
    (acc, apuracao) => {
      const periodoFimMs = new Date(apuracao.periodoFim).getTime();
      const fechadoEmMs = apuracao.fechadoEm ? new Date(apuracao.fechadoEm).getTime() : 0;

      if (apuracao.status === 'ABERTO' || apuracao.status === 'REABERTO') {
        acc.totalAbertos += 1;
        acc.totalCobravelPrevisto += apuracao.totalCobravel;
        if (periodoFimMs - now <= sevenDaysMs && periodoFimMs - now >= 0) {
          acc.vencendoEmAteSeteDias += 1;
        }
      }

      if (apuracao.status === 'FECHADO' && fechadoEmMs >= startOfMonthMs) {
        acc.fechadosNoMes += 1;
      }

      if (apuracao.eventosRejeitadosCount > 0) {
        acc.comErros += 1;
      }

      return acc;
    },
    { ...DEFAULT_APURACAO_SUMMARY },
  );
};

const apuracaoSlice = createSlice({
  name: 'apuracao',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<ApuracaoListFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = DEFAULT_APURACAO_FILTERS;
    },
    setEventosRejeitadosFilters: (
      state,
      action: PayloadAction<Partial<EventosRejeitadosFilters>>,
    ) => {
      state.eventosRejeitadosFilters = { ...state.eventosRejeitadosFilters, ...action.payload };
    },
    clearEventosRejeitadosFilters: (state) => {
      state.eventosRejeitadosFilters = DEFAULT_EVENTOS_REJEITADOS_FILTERS;
    },
    clearEventoDetalhe: (state) => {
      state.eventoDetalhe = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchApuracoes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchApuracoes.fulfilled, (state, action: PayloadAction<Apuracao[]>) => {
        state.loading = false;
        state.apuracoes = action.payload;
        state.summary = computeSummary(action.payload);
      })
      .addCase(fetchApuracoes.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? 'Erro ao carregar apurações';
      })

      .addCase(fetchApuracaoById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchApuracaoById.fulfilled, (state, action: PayloadAction<Apuracao>) => {
        state.loading = false;
        const idx = state.apuracoes.findIndex((a) => a.id === action.payload.id);
        if (idx >= 0) {
          state.apuracoes[idx] = action.payload;
        } else {
          state.apuracoes.push(action.payload);
        }
        state.summary = computeSummary(state.apuracoes);
      })
      .addCase(fetchApuracaoById.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? 'Erro ao carregar apuração';
      })

      .addCase(fetchEventoDetalhe.pending, (state) => {
        state.eventoDetalheLoading = true;
        state.eventoDetalhe = null;
        state.error = null;
      })
      .addCase(fetchEventoDetalhe.fulfilled, (state, action: PayloadAction<EventoConsumo>) => {
        state.eventoDetalheLoading = false;
        state.eventoDetalhe = action.payload;
      })
      .addCase(fetchEventoDetalhe.rejected, (state, action) => {
        state.eventoDetalheLoading = false;
        state.error = (action.payload as string) ?? 'Erro ao carregar detalhe do evento';
      })

      .addCase(fetchRejeitadosDoCiclo.pending, (state) => {
        state.rejectedInvoicesLoading = true;
        state.error = null;
      })
      .addCase(
        fetchRejeitadosDoCiclo.fulfilled,
        (state, action: PayloadAction<RejectedInvoice[]>) => {
          state.rejectedInvoicesLoading = false;
          state.rejectedInvoices = action.payload;
        },
      )
      .addCase(fetchRejeitadosDoCiclo.rejected, (state, action) => {
        state.rejectedInvoicesLoading = false;
        state.error = (action.payload as string) ?? 'Erro ao carregar faturas rejeitadas';
      })

      .addCase(reemitirFaturaRejeitada.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(
        reemitirFaturaRejeitada.fulfilled,
        (state, action: PayloadAction<RejectedInvoice[]>) => {
          state.saving = false;
          state.rejectedInvoices = action.payload;
        },
      )
      .addCase(reemitirFaturaRejeitada.rejected, (state, action) => {
        state.saving = false;
        state.error = (action.payload as string) ?? 'Erro ao reemitir fatura';
      })

      .addCase(fecharCicloApuracao.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(fecharCicloApuracao.fulfilled, (state, action: PayloadAction<Apuracao>) => {
        state.saving = false;
        state.apuracoes = state.apuracoes.map((a) =>
          a.id === action.payload.id ? action.payload : a,
        );
        state.summary = computeSummary(state.apuracoes);
      })
      .addCase(fecharCicloApuracao.rejected, (state, action) => {
        state.saving = false;
        state.error = (action.payload as string) ?? 'Erro ao fechar ciclo';
      })

      .addCase(reabrirCicloApuracao.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(reabrirCicloApuracao.fulfilled, (state, action: PayloadAction<Apuracao>) => {
        state.saving = false;
        state.apuracoes = state.apuracoes.map((a) =>
          a.id === action.payload.id ? action.payload : a,
        );
        state.summary = computeSummary(state.apuracoes);
      })
      .addCase(reabrirCicloApuracao.rejected, (state, action) => {
        state.saving = false;
        state.error = (action.payload as string) ?? 'Erro ao reabrir ciclo';
      })

      .addCase(marcarApuracaoFaturada.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(marcarApuracaoFaturada.fulfilled, (state, action: PayloadAction<Apuracao>) => {
        state.saving = false;
        state.apuracoes = state.apuracoes.map((a) =>
          a.id === action.payload.id ? action.payload : a,
        );
      })
      .addCase(marcarApuracaoFaturada.rejected, (state, action) => {
        state.saving = false;
        state.error = (action.payload as string) ?? 'Erro ao marcar apuração como faturada';
      })

      .addCase(reprocessarEventos.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(reprocessarEventos.fulfilled, (state, action: PayloadAction<EventoConsumo[]>) => {
        state.saving = false;
        const reprocessadosIds = new Set(action.payload.map((e) => e.id));
        state.apuracoes = state.apuracoes.map((apuracao) => ({
          ...apuracao,
          eventos: apuracao.eventos.map((evento) =>
            reprocessadosIds.has(evento.id)
              ? {
                  ...evento,
                  status: 'APURADO' as const,
                  motivoRejeicao: null,
                  sugestaoCorrecao: null,
                }
              : evento,
          ),
        }));
        state.summary = computeSummary(state.apuracoes);
      })
      .addCase(reprocessarEventos.rejected, (state, action) => {
        state.saving = false;
        state.error = (action.payload as string) ?? 'Erro ao reprocessar eventos';
      });
  },
});

export const {
  setFilters,
  clearFilters,
  setEventosRejeitadosFilters,
  clearEventosRejeitadosFilters,
  clearEventoDetalhe,
  clearError,
} = apuracaoSlice.actions;
export const apuracaoReducer = apuracaoSlice.reducer;
